/**
 * OUTSIDE-PRESS / ESCAPE DISMISS for the desk's anchored menus and panels. One hook, three
 * users (the Display bar's chips, the Views menu, the panel's mini-menus since 0.1.455).
 *
 * `pointerdown`, not `click`, on purpose: the press that OPENS a menu has already finished its
 * pointerdown by the time React commits the open state and this effect registers, so it can
 * never be mistaken for a press "elsewhere" — the trap `platform/dismiss-on-outside-click.ts`
 * documents for `click` does not arise here.
 *
 * `capture`: a nested menu (the panel's axis picker inside the open panel) registers in the
 * capture phase and STOPS the Escape it consumes, so one Escape closes the innermost surface
 * and a second closes the panel — the order a person expects, and the reverse of what two
 * bubble-phase listeners on `document` would do (the outer one was registered first).
 */
import { useEffect, useRef, type RefObject } from 'react';

export function useDismiss(
  open: boolean,
  close: () => void,
  options: { capture?: boolean } = {},
): RefObject<HTMLDivElement> {
  const ref = useRef<HTMLDivElement>(null);
  const capture = options.capture === true;
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent): void => {
      if (ref.current !== null && e.target instanceof Node && !ref.current.contains(e.target)) close();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (capture) e.stopPropagation();
      close();
    };
    document.addEventListener('pointerdown', onDown, { capture });
    document.addEventListener('keydown', onKey, { capture });
    return () => {
      document.removeEventListener('pointerdown', onDown, { capture });
      document.removeEventListener('keydown', onKey, { capture });
    };
  }, [open, close, capture]);
  return ref;
}
